// File: src/DVLAPreviewPage.js 
// OOOSH Driver Verification - DVLA Check Upload Page
// Uses Claude Vision API for validation with real-time preview
// Updated: February 2026
// SESSION 76: Added tolerant licence ending matching, simplified excess display
// SESSION 82: Added resilience improvements - timeout handling, auto-recovery, better error states
// SESSION 92: Added client-side error reporting + file size auto-compression safety net
// SESSION 96: Licence ending matching via PDF text extraction (100% accurate, no OCR)
// SESSION 97: Full PDF text extraction — ALL fields extracted from text layer, bypassing Claude Vision for PDFs
// SESSION 98: Robustness for DVLA wording changes —
//   (1) date extraction now anchors on "Date summary" and accepts any verb
//       (DVLA switched "Date summary generated" → "Date summary viewed"),
//   (2) if PDF text extraction can't pull every critical field, fall back to
//       Claude Vision instead of hard-rejecting a valid document, and emit a
//       warning so we get early notice of future DVLA format changes.
// Oct 2026 redesign: StepShell (Final checks & sign) + hf- building blocks
//   (src/ui/HireFormUI.js). Presentation only — states, timers, parsing and
//   the upload-at-validation rule are unchanged.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import reportError from './utils/errorReporter';
import StuckDriverHelper from './StuckDriverHelper';
import { StepShell, Card, PrimaryButton, Note, Spinner, SummaryRow, PHASE } from './ui/HireFormUI';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ============================================================================
// CONFIGURATION
// ============================================================================
const REQUEST_TIMEOUT_MS = 30000;  // 30 seconds for API request
const PROCESSING_TIMEOUT_MS = 45000;  // 45 seconds max processing time

// ============================================================================
// SESSION 97: DATE PARSING HELPER
// Converts DVLA date text like "18 February 2026" to "2026-02-18"
// ============================================================================
const MONTHS = {
  'january': '01', 'february': '02', 'march': '03', 'april': '04',
  'may': '05', 'june': '06', 'july': '07', 'august': '08',
  'september': '09', 'october': '10', 'november': '11', 'december': '12'
};

function parseDvlaDateText(dateText) {
  if (!dateText) return null;
  const match = dateText.match(/(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i);
  if (!match) return null;
  const day = match[1].padStart(2, '0');
  const month = MONTHS[match[2].toLowerCase()];
  const year = match[3];
  if (!month) return null;
  return `${year}-${month}-${day}`;
}

const GOV_UK_RECORD_URL = 'https://www.viewdrivingrecord.service.gov.uk/driving-record/licence-number';
const DVLA_HELP_GUIDE_URL = 'https://www.oooshtours.co.uk/how-to-get-a-dvla-check-code';

const pointsLabel = (points) => `${points} point${points !== 1 ? 's' : ''}`;

const DVLAPreviewPage = ({ 
  email, 
  jobId,
  driverName,
  licenseEnding,
  onComplete, 
  onBack 
}) => {
  const [uploadState, setUploadState] = useState('idle');
  const [previewImage, setPreviewImage] = useState(null);
  const [rawFileData, setRawFileData] = useState(null);
  const [rawFileIsPdf, setRawFileIsPdf] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [insuranceDecision, setInsuranceDecision] = useState(null);
  const [calculatedExcess, setCalculatedExcess] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processingElapsed, setProcessingElapsed] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  // Only stops a second press while Continue saves + waits for the upload;
  // handleContinue itself is unchanged.
  const [continuing, setContinuing] = useState(false);
  
  const fileInputRef = useRef(null);
  const processingTimeoutRef = useRef(null);
  // The evidence-copy upload to OP. Fired the moment validation succeeds (NOT
  // on Continue — see uploadDvlaDocument) and awaited by handleContinue so the
  // driver can't outrun it. `done` stops Continue re-sending a file that's
  // already on the record; `promise` lets Continue wait for one in flight.
  const dvlaUploadRef = useRef({ done: false, promise: null, payload: null });
  const elapsedIntervalRef = useRef(null);

  // ============================================================================
  // SESSION 82: Processing timeout auto-recovery
  // ============================================================================
  useEffect(() => {
    // Clear any existing timers on state change
    if (uploadState !== 'processing' && uploadState !== 'uploading') {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      setProcessingElapsed(0);
      return;
    }
    
    // Start elapsed time counter
    if (uploadState === 'processing' || uploadState === 'uploading') {
      elapsedIntervalRef.current = setInterval(() => {
        setProcessingElapsed(prev => prev + 1);
      }, 1000);
      
      // Set auto-recovery timeout
      processingTimeoutRef.current = setTimeout(() => {
        console.error('⏱️ Processing timeout - auto-recovering');
        reportError({
          page: 'DVLAPreviewPage',
          error: 'Processing timeout after ' + PROCESSING_TIMEOUT_MS + 'ms',
          severity: 'warning',
          email
        });
        
        setUploadState('error');
        setErrorMessage(
          'Processing is taking longer than expected. This might be due to a slow connection. ' +
          'Please try uploading your document again.'
        );
      }, PROCESSING_TIMEOUT_MS);
    }
    
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, [uploadState, email]);

  // ============================================================================
  // SESSION 82: Fetch with timeout wrapper
  // ============================================================================
  const fetchWithTimeout = async (url, options, timeoutMs = REQUEST_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  // ============================================================================
  // SESSION 92: Image compression (same pattern as POA1/POA2)
  // Reduces image size for API - auto-retries with lower quality
  // ============================================================================
  const compressImage = async (base64Data, maxWidth = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if wider than maxWidth
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        const compressedBase64 = compressedDataUrl.split(',')[1];
        
        console.log(`📦 Image compressed: ${Math.round(base64Data.length * 0.75 / 1024)}KB → ${Math.round(compressedBase64.length * 0.75 / 1024)}KB`);
        
        resolve(compressedBase64);
      };
      img.src = `data:image/jpeg;base64,${base64Data}`;
    });
  };

  // Excess calculation
  const calculateTotalExcess = useCallback((decision) => {
    const BASE_EXCESS = 1000;
    const VAT_RATE = 0.20;
    
    const additionalExcess = decision?.excess || 0;
    const totalBeforeVat = BASE_EXCESS + additionalExcess;
    const vatAmount = totalBeforeVat * VAT_RATE;
    const totalWithVat = totalBeforeVat + vatAmount;
    
    return {
      base: BASE_EXCESS,
      additional: additionalExcess,
      totalBeforeVat: totalBeforeVat,
      vat: vatAmount,
      totalWithVat: totalWithVat,
      formatted: `£${totalWithVat.toLocaleString('en-GB')}`
    };
  }, []);

  // Handle file selection
  // ==========================================================================
  // Evidence-copy upload to OP (Sep 2026, Steven Aldridge / job 16116)
  //
  // The DVLA DATA is written server-side by document-processor during
  // validation. The DVLA FILE used to be uploaded only inside handleContinue,
  // i.e. only if the driver clicked Continue. But once the data has landed,
  // OP's router already considers the DVLA step complete — so any reload,
  // back-button or stray tap between "validated" and "Continue" sent the driver
  // straight to Signature and the file was never uploaded. Nothing failed and
  // nothing was logged; the record just had a check date and no document.
  //
  // So the upload now fires the moment validation succeeds, and Continue only
  // waits for it (or retries it if it failed). Two attempts, then a critical
  // telemetry report so a genuine failure is visible in OP rather than a
  // console.warn nobody sees — same pattern as POA1.
  //
  // Images send the compressed copy (the one Claude already read) — the raw
  // camera original could exceed Netlify's 6MB function payload limit. PDFs
  // (the normal case — the DVLA summary is generated as a PDF) go as-is.
  // ==========================================================================
  // `slot` is the ref object current when the upload STARTED, so a completion
  // from a document the driver has since replaced can't mark the new one done.
  const uploadDvlaDocument = async (base64Data, isPdf, slot) => {
    if (!base64Data) return false;
    const ext = isPdf ? 'pdf' : 'jpg';
    const contentType = isPdf ? 'application/pdf' : 'image/jpeg';
    const approxBytes = Math.round(base64Data.length * 0.75);
    console.log(`📤 Uploading DVLA document to OP... (~${Math.round(approxBytes / 1024)}KB, ${ext})`);

    const attemptUpload = async (attempt) => {
      const uploadResponse = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upload-file-board-a',
          email: email,
          fileType: 'dvla',
          fileData: base64Data,
          filename: `dvla_check_${Date.now()}.${ext}`,
          contentType,
          clearExisting: true
        })
      });
      if (uploadResponse.ok) {
        console.log(`✅ DVLA document uploaded (attempt ${attempt})`);
        return true;
      }
      const bodyText = await uploadResponse.text().catch(() => '');
      console.error(`⚠️ DVLA upload attempt ${attempt} failed: HTTP ${uploadResponse.status} — ${bodyText.slice(0, 500)}`);
      return { status: uploadResponse.status, body: bodyText };
    };

    let lastFailure = null;
    try {
      const first = await attemptUpload(1);
      if (first === true) { slot.done = true; return true; }
      lastFailure = first;
      const retryable = !first.status || first.status === 429 || first.status >= 500;
      if (retryable) {
        await new Promise(r => setTimeout(r, 1500));
        const second = await attemptUpload(2);
        if (second === true) { slot.done = true; return true; }
        lastFailure = second;
      }
    } catch (uploadError) {
      console.error('⚠️ DVLA upload threw:', uploadError);
      lastFailure = { status: 0, body: uploadError?.message || String(uploadError) };
    }

    reportError({
      page: 'DVLAPreviewPage',
      error: `dvla upload failed: HTTP ${lastFailure?.status} — ${(lastFailure?.body || '').slice(0, 300)}`,
      severity: 'critical',
      email,
      additionalInfo: `approxBytes=${approxBytes} ext=${ext}`
    });
    return false;
  };

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    
    console.log('📄 File selected:', file.name, file.type);
    setUploadState('uploading');
    setErrorMessage(null);
    setExtractedData(null);
    setInsuranceDecision(null);
    setCalculatedExcess(null);
    
    try {
      let imageBase64;
      let rawBase64;
      let pdfExtractedData = null; // SESSION 97: all data extracted from PDF text layer
      
      if (file.type === 'application/pdf') {
        console.log('📄 Converting PDF to image...');
        imageBase64 = await convertPdfToImage(file);
        rawBase64 = await fileToBase64(file);
        
        // SESSION 97: Extract ALL data directly from PDF text layer
        // This is 100% accurate — real text data, not OCR
        pdfExtractedData = await extractAllDataFromPdf(file);
        console.log('📝 PDF text extraction result:', {
          driverName: pdfExtractedData?.driverName || 'MISSING',
          dateGenerated: pdfExtractedData?.dateGenerated || 'MISSING',
          checkCode: pdfExtractedData?.checkCode ? 'found' : 'MISSING',
          totalPoints: pdfExtractedData?.totalPoints,
          endorsements: pdfExtractedData?.endorsements?.length || 0,
          licenceEnding: pdfExtractedData?.licenceEnding || 'MISSING'
        });
      } else if (file.type.startsWith('image/')) {
        imageBase64 = await fileToBase64(file);
        rawBase64 = imageBase64;
        // No PDF text extraction for images — server will use Claude Vision as fallback
      } else {
        throw new Error('Please upload a PDF or image file (JPG, PNG)');
      }
      
      // SESSION 96/97: Client-side licence ending check using PDF text (not OCR)
      // Compare the text-extracted ending against what Idenfy gave us
      const pdfLicenceEnding = pdfExtractedData?.licenceEnding || null;
      
      if (licenseEnding && pdfLicenceEnding) {
        const normalize = (str) => str.toUpperCase().replace(/[^A-Z0-9]/g, '');
        // Strip issue number (last 1-2 digits if present) from expected value
        const stripIssue = (str) => str.length > 5 && /\d{1,2}$/.test(str) ? str.replace(/\d{1,2}$/, '') : str;
        
        const expected = stripIssue(normalize(licenseEnding));
        const actual = normalize(pdfLicenceEnding);
        
        console.log('🔍 Licence ending comparison (PDF text vs Idenfy):');
        console.log('   Expected (from Idenfy):', expected);
        console.log('   Actual (from PDF text):', actual);
        
        const matches = expected === actual || 
                        expected.includes(actual) || 
                        actual.includes(expected);
        
        if (!matches) {
          console.log('❌ Licence ending MISMATCH — wrong document');
          reportError({
            page: 'DVLAPreviewPage',
            error: `Licence ending mismatch (PDF text): expected=${licenseEnding}, found=${pdfLicenceEnding}`,
            severity: 'warning',
            email
          });
          setPreviewImage(imageBase64);
          setRawFileData(rawBase64);
          setUploadState('error');
          setErrorMessage('The licence number on this DVLA check does not match your driving licence. Please ensure you are uploading the correct document.');
          return;
        }
        console.log('✅ Licence ending MATCH confirmed (PDF text — 100% accurate)');
      } else if (licenseEnding && !pdfLicenceEnding) {
        // Could not extract text — not a blocker, server will handle other validation
        console.log('⚠️ Could not extract licence ending from PDF text — skipping client-side check');
      }
      
      // ================================================================
      // SESSION 92: File size safety net — auto-compress if too large
      // Still needed for image uploads (Claude Vision fallback) and for
      // the preview image / Monday.com file upload
      // ================================================================
      const MAX_SIZE_BYTES = 4 * 1024 * 1024; // 4MB
      let sizeInBytes = imageBase64.length * 0.75; // Base64 → actual bytes
      
      if (sizeInBytes > MAX_SIZE_BYTES) {
        console.log(`📦 Image too large (${Math.round(sizeInBytes / 1024)}KB), auto-compressing...`);
        
        // Pass 1: Moderate compression
        imageBase64 = await compressImage(imageBase64, 1600, 0.75);
        sizeInBytes = imageBase64.length * 0.75;
        
        // Pass 2: More aggressive if still too large
        if (sizeInBytes > MAX_SIZE_BYTES) {
          console.log(`📦 Still too large (${Math.round(sizeInBytes / 1024)}KB), compressing further...`);
          imageBase64 = await compressImage(imageBase64, 1200, 0.65);
          sizeInBytes = imageBase64.length * 0.75;
          
          // Pass 3: Maximum compression
          if (sizeInBytes > MAX_SIZE_BYTES) {
            console.log(`📦 Still too large (${Math.round(sizeInBytes / 1024)}KB), maximum compression...`);
            imageBase64 = await compressImage(imageBase64, 1000, 0.5);
            sizeInBytes = imageBase64.length * 0.75;
            
            // If STILL too large after 3 passes, log it but try anyway
            if (sizeInBytes > MAX_SIZE_BYTES) {
              console.warn(`⚠️ Image still ${Math.round(sizeInBytes / 1024)}KB after 3 compression passes — attempting anyway`);
              reportError({
                page: 'DVLAPreviewPage',
                error: `Image still ${Math.round(sizeInBytes / 1024)}KB after 3 compression passes`,
                severity: 'warning',
                email
              });
            }
          }
        }
        
        console.log(`✅ Final image size: ${Math.round(sizeInBytes / 1024)}KB`);
      }
      
      const isPdf = file.type === 'application/pdf';
      setPreviewImage(imageBase64);
      setRawFileData(rawBase64);
      setRawFileIsPdf(isPdf);
      setUploadState('processing');
      // A fresh document — forget any earlier upload state.
      dvlaUploadRef.current = { done: false, promise: null, payload: null };
      
      console.log('🔍 Sending to DVLA processor...');
      
      // SESSION 97: Send PDF-extracted data to server
      // If pdfExtractedData is complete, server skips Claude Vision entirely (faster + 100% accurate)
      // If pdfExtractedData is null/incomplete (image upload), server uses Claude Vision as fallback
      const requestBody = {
        action: 'dvla',
        imageData: imageBase64,
        email: email
      };
      
      // SESSION 98: Only trust PDF text extraction when it produced every
      // critical field. If DVLA tweaks a label and we drop checkCode / name /
      // date, fall back to Claude Vision on the rendered image (already sent as
      // imageData) rather than hard-rejecting a valid document. We also report
      // the gap so we get early warning of a DVLA format change.
      const CRITICAL_PDF_FIELDS = ['checkCode', 'driverName', 'dateGenerated'];
      const missingCriticalFields = pdfExtractedData
        ? CRITICAL_PDF_FIELDS.filter(f => !pdfExtractedData[f])
        : CRITICAL_PDF_FIELDS;

      if (pdfExtractedData && missingCriticalFields.length === 0) {
        requestBody.pdfExtractedData = pdfExtractedData;
        console.log('📝 Sending PDF-extracted data to server (Claude Vision will be skipped)');
      } else if (pdfExtractedData) {
        // We had PDF text but couldn't pull every critical field — likely a DVLA
        // wording change. Let Claude Vision handle it and flag for investigation.
        console.warn('⚠️ PDF text extraction incomplete, missing:', missingCriticalFields.join(', '),
          '— falling back to Claude Vision');
        reportError({
          page: 'DVLAPreviewPage',
          error: `PDF text extraction incomplete (missing: ${missingCriticalFields.join(', ')}) — DVLA format may have changed. Falling back to Claude Vision.`,
          severity: 'warning',
          email
        });
      } else {
        console.log('🖼️ Image upload — server will use Claude Vision for extraction');
      }
      
      // SESSION 82: Use fetch with timeout
      const validationResponse = await fetchWithTimeout('/.netlify/functions/document-processor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!validationResponse.ok) {
        const errorText = await validationResponse.text().catch(() => 'Unknown error');
        // SESSION 92: Report API errors
        reportError({
          page: 'DVLAPreviewPage',
          error: `API error ${validationResponse.status}: ${errorText.substring(0, 200)}`,
          severity: 'warning',
          email
        });
        throw new Error('DVLA check service unavailable. Please try again.');
      }
      
      const validationData = await validationResponse.json();
      console.log('✅ DVLA validation result:', validationData);
      
      const result = validationData.result;
      setExtractedData(result);
      setInsuranceDecision(result?.insuranceDecision || null);
      
      if (result?.insuranceDecision?.approved) {
        const excess = calculateTotalExcess(result.insuranceDecision);
        setCalculatedExcess(excess);
        console.log('💰 Calculated excess:', excess);
      }
      
      if (result?.isValid && result?.extractionSuccess) {
        setUploadState('validated');
        // Upload the evidence copy NOW, not on Continue. PDFs go as-is; images
        // send the (possibly compressed) copy Claude read rather than the raw
        // original.
        const payload = { data: isPdf ? rawBase64 : imageBase64, isPdf };
        dvlaUploadRef.current.payload = payload;
        dvlaUploadRef.current.promise = uploadDvlaDocument(payload.data, payload.isPdf, dvlaUploadRef.current);
      } else {
        setUploadState('error');
        setErrorMessage(
          result?.issues?.join('. ') || 
          'Could not validate DVLA check. Please ensure you have uploaded a valid DVLA share code check.'
        );
      }
      
    } catch (err) {
      console.error('❌ Upload/validation error:', err);
      // SESSION 92: Report upload/validation errors
      reportError({
        page: 'DVLAPreviewPage',
        error: err.message || 'Unknown error during upload',
        severity: 'warning',
        email
      });
      setFailureCount(prev => prev + 1);
      setUploadState('error');
      setErrorMessage(err.message || 'Failed to process document. Please try again.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, licenseEnding, calculateTotalExcess]);

  // Convert PDF to image (still needed for preview and Monday.com file upload)
  const convertPdfToImage = async (pdfFile) => {
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    
    console.log(`📑 PDF has ${numPages} pages - processing all pages`);
    
    const scale = 1.5;
    const canvases = [];
    let totalHeight = 0;
    let maxWidth = 0;
    
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      const context = canvas.getContext('2d');
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      canvases.push(canvas);
      totalHeight += viewport.height;
      maxWidth = Math.max(maxWidth, viewport.width);
      
      console.log(`✅ Rendered page ${pageNum}/${numPages}`);
    }
    
    const combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = maxWidth;
    combinedCanvas.height = totalHeight;
    const combinedContext = combinedCanvas.getContext('2d');
    
    combinedContext.fillStyle = 'white';
    combinedContext.fillRect(0, 0, maxWidth, totalHeight);
    
    let yOffset = 0;
    canvases.forEach(canvas => {
      combinedContext.drawImage(canvas, 0, yOffset);
      yOffset += canvas.height;
    });
    
    const dataUrl = combinedCanvas.toDataURL('image/jpeg', 0.85);
    console.log(`✅ All ${numPages} pages combined into single image`);
    return dataUrl.split(',')[1];
  };

  // ============================================================================
  // SESSION 97: Extract ALL data from PDF using PDF.js text layer
  // This gives us the EXACT text from the PDF — no OCR, no guessing.
  // Returns a complete data object that lets the server skip Claude Vision entirely.
  // ============================================================================
  const extractAllDataFromPdf = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      // Extract text from ALL pages
      const pageTexts = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        pageTexts.push(pageText);
      }
      
      const fullText = pageTexts.join('\n\n');
      console.log('📝 PDF full text extracted, length:', fullText.length, 'pages:', pdf.numPages);
      
      // --- Licence ending (same as Session 96) ---
      let licenceEnding = null;
      const licenceMatch = fullText.match(/X{7,8}([A-Z0-9]{5,10})/i);
      if (licenceMatch) {
        licenceEnding = licenceMatch[1];
      }
      
      // --- Driver name ---
      // Pattern: "Driver's full name" followed by the name, then "Date summary generated"
      let driverNameValue = null;
      const nameMatch = fullText.match(/(?:Driver'?s?\s+full\s+name|full\s+name)\s+(.+?)\s+Date\s+summary/i);
      if (nameMatch) {
        driverNameValue = nameMatch[1].trim();
      }
      
      // --- Date the summary was produced ---
      // DVLA has changed this label over time: older docs say "Date summary
      // generated", newer ones say "Date summary viewed". Rather than hard-code
      // the verb, anchor on "Date summary" and grab the first date that follows
      // it (capped to a short window so we can't drift into "Licence valid
      // from/to"). This tolerates any future verb tweak without code changes.
      let dateGenerated = null;
      const dateMatch = fullText.match(/Date\s+summary\b[\s\S]{0,40}?(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
      if (dateMatch) {
        dateGenerated = parseDvlaDateText(dateMatch[1]);
      }
      
      // --- Check code ---
      // Format: "QH Ht g6 Zq" — case-sensitive, 4 pairs separated by spaces
      let checkCode = null;
      const checkCodeMatch = fullText.match(/check\s+code:?\s*([A-Za-z0-9]{2}\s+[A-Za-z0-9]{2}\s+[A-Za-z0-9]{2}\s+[A-Za-z0-9]{2})/i);
      if (checkCodeMatch) {
        checkCode = checkCodeMatch[1];
      }
      
      // --- Driving status ---
      let drivingStatus = null;
      if (/Current\s+full\s+licence/i.test(fullText)) {
        drivingStatus = 'Current full licence';
      } else if (/Current\s+provisional\s+licence/i.test(fullText)) {
        drivingStatus = 'Current provisional licence';
      }
      
      // --- Total points (from page 1 summary) ---
      // Page 1 shows "N Points" in the endorsements header area
      let totalPoints = 0;
      const pointsMatch = pageTexts[0]?.match(/(\d+)\s+Points?/i);
      if (pointsMatch) {
        totalPoints = parseInt(pointsMatch[1], 10);
      }
      
      // --- Licence validity dates ---
      let licenceValidFrom = null;
      const validFromMatch = fullText.match(/Licence\s+valid\s+from\s+(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
      if (validFromMatch) {
        licenceValidFrom = parseDvlaDateText(validFromMatch[1]);
      }
      
      let licenceValidTo = null;
      const validToMatch = fullText.match(/Licence\s+valid\s+to\s+(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
      if (validToMatch) {
        licenceValidTo = parseDvlaDateText(validToMatch[1]);
      }
      
      // --- Endorsements (from page 2+) ---
      // Each endorsement has: description (CODE), Penalty points: N, dates
      const endorsements = [];
      const codeMatches = [...fullText.matchAll(/\(([A-Z]{2}\d{2})\)/g)];
      
      for (const match of codeMatches) {
        const code = match[1];
        // Get surrounding text for context (300 chars after the code)
        const afterCode = fullText.substring(match.index, Math.min(fullText.length, match.index + 400));
        
        // Extract points
        const ePtsMatch = afterCode.match(/Penalty\s+points:?\s*(\d+)/i);
        const ePoints = ePtsMatch ? parseInt(ePtsMatch[1], 10) : 3;
        
        // Extract offence date
        const offDateMatch = afterCode.match(/Offence\s+date\s+(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
        const offenceDate = offDateMatch ? parseDvlaDateText(offDateMatch[1]) : null;
        
        // Extract penalty point expiry date
        const expMatch = afterCode.match(/Penalty\s+point\s+expiry\s+date\s+(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
        const expiryDate = expMatch ? parseDvlaDateText(expMatch[1]) : null;
        
        endorsements.push({
          code: code,
          points: ePoints,
          offenceDate: offenceDate,
          expiryDate: expiryDate
        });
      }
      
      // --- Disqualification check ---
      const hasDisqualification = /disqualif/i.test(fullText);
      
      // Build the result object
      const result = {
        licenceEnding: licenceEnding,
        driverName: driverNameValue,
        dateGenerated: dateGenerated,
        checkCode: checkCode,
        totalPoints: totalPoints,
        drivingStatus: drivingStatus,
        licenceValidFrom: licenceValidFrom,
        licenceValidTo: licenceValidTo,
        endorsements: endorsements,
        hasDisqualification: hasDisqualification,
        isPdfExtraction: true  // Flag so server knows this came from PDF text, not Claude Vision
      };
      
      console.log('✅ PDF text extraction complete:', {
        driverName: result.driverName || 'MISSING',
        dateGenerated: result.dateGenerated || 'MISSING',
        checkCode: result.checkCode ? 'found' : 'MISSING',
        totalPoints: result.totalPoints,
        endorsements: result.endorsements.length,
        drivingStatus: result.drivingStatus || 'MISSING',
        licenceEnding: result.licenceEnding || 'MISSING'
      });
      
      return result;
      
    } catch (err) {
      console.warn('⚠️ PDF text extraction failed:', err.message);
      reportError({
        page: 'DVLAPreviewPage',
        error: `PDF text extraction failed: ${err.message}`,
        severity: 'info',
        email
      });
      return null;
    }
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  // Reset
  const handleReset = () => {
    setUploadState('idle');
    setPreviewImage(null);
    setRawFileData(null);
    setRawFileIsPdf(false);
    dvlaUploadRef.current = { done: false, promise: null, payload: null };
    setExtractedData(null);
    setInsuranceDecision(null);
    setCalculatedExcess(null);
    setErrorMessage(null);
    setProcessingElapsed(0);
  };

  // Continue to next step
  const handleContinue = async () => {
    if (extractedData?.isValid) {
      try {
        const excessToSave = calculatedExcess?.formatted || '£1,200';
        
        console.log('💾 Saving DVLA data:', {
          dvlaValidUntil: calculateValidUntil(),
          dvlaPoints: extractedData.totalPoints || 0,
          dvlaEndorsements: extractedData.endorsements?.length || 0,
          dvlaCheckCode: extractedData.checkCode ? 'present' : 'missing',
          dvlaCalculatedExcess: excessToSave
        });
        
        const dvlaUpdates = {
          dvlaValidUntil: calculateValidUntil(),
          dvlaPoints: extractedData.totalPoints || 0,
          dvlaEndorsements: extractedData.endorsements?.length > 0
            ? extractedData.endorsements.map(e => ({
                code: e.code,
                points: e.points,
                date: e.offenceDate || null,
                expiry: e.expiryDate || null
              }))
            : [],
          dvlaCheckCode: extractedData.checkCode || null,
          dvlaCheckDate: extractedData.dateGenerated || new Date().toISOString().split('T')[0],
          dvlaCalculatedExcess: excessToSave,
          lastUpdated: new Date().toISOString().split('T')[0]
        };

        // Add referral fields when manual review is required
        if (insuranceDecision?.manualReview) {
          dvlaUpdates.requiresReferral = true;
          dvlaUpdates.referralStatus = 'pending';
          dvlaUpdates.referralReasons = insuranceDecision.reasons || ['DVLA check requires manual insurance review'];
          dvlaUpdates.overallStatus = 'Insurance Review';
        }

        // This write carries the points, endorsements, calculated excess and
        // any points-triggered referral flag — the DVLA DATA. It failed
        // silently for every driver WITH endorsements from Apr–Sep 2026 (OP
        // rejected the endorsements array as JSON; the response was never
        // read). Still never a gate for the driver, but it must be LOUD.
        const dvlaDataResponse = await fetch('/.netlify/functions/monday-integration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update-driver-board-a',
            email: email,
            updates: dvlaUpdates
          })
        });
        if (!dvlaDataResponse.ok) {
          const body = await dvlaDataResponse.text().catch(() => '');
          console.error('❌ DVLA data write failed:', dvlaDataResponse.status, body.slice(0, 300));
          reportError({
            page: 'DVLAPreviewPage',
            error: `DVLA data write failed (${dvlaDataResponse.status}): ${body.slice(0, 300)}`,
            severity: 'critical',
            email,
            additionalInfo: {
              points: dvlaUpdates.dvlaPoints,
              endorsements: (dvlaUpdates.dvlaEndorsements || []).length,
              requiresReferral: !!dvlaUpdates.requiresReferral,
            }
          });
        }
        
        // The evidence copy was uploaded at validation time. Wait for it if it's
        // still in flight; give it one more go if it failed. Never blocks the
        // driver — a still-missing file is reported via telemetry, not a gate.
        const upload = dvlaUploadRef.current;
        if (!upload.done) {
          if (upload.promise) await upload.promise;
          if (!upload.done) {
            const payload = upload.payload || (rawFileData ? { data: rawFileData, isPdf: rawFileIsPdf } : null);
            if (payload) await uploadDvlaDocument(payload.data, payload.isPdf, upload);
          }
        }
        
        console.log('✅ DVLA data saved to Monday.com');
      } catch (err) {
        console.error('Error saving DVLA results:', err);
        // SESSION 92: Report save errors but don't block the user
        reportError({
          page: 'DVLAPreviewPage',
          error: `Failed to save DVLA results: ${err.message}`,
          severity: 'warning',
          email
        });
      }
      
      onComplete({
        points: extractedData.totalPoints || 0,
        endorsements: extractedData.endorsements || [],
        insuranceDecision: insuranceDecision,
        calculatedExcess: calculatedExcess,
        validUntil: calculateValidUntil()
      });
    }
  };

  // Calculate validity (30 days)
  const calculateValidUntil = () => {
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + 30);
    return validUntil.toISOString().split('T')[0];
  };

  // Get excess explanation based on points
  const getExcessExplanation = () => {
    const points = extractedData?.totalPoints || 0;
    const endorsements = extractedData?.endorsements || [];
    
    if (points === 0) {
      return 'Clean licence - standard excess applies.';
    } else if (points <= 6) {
      if (points === 6 && endorsements.length === 1) {
        const code = endorsements[0]?.code;
        if (['SP30', 'SP50', 'CU80'].includes(code)) {
          return `Single 6-point ${code} offence - additional £1,000 excess applies.`;
        }
      }
      return 'Minor points - standard excess applies.';
    } else if (points === 9) {
      return 'Three 3-point offences - additional £500 excess applies.';
    }
    return 'Based on your driving record.';
  };

  // Format elapsed time for display
  const formatElapsedTime = (seconds) => {
    if (seconds < 10) return 'a few seconds';
    if (seconds < 30) return `${seconds} seconds`;
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const handleContinueClick = async () => {
    setContinuing(true);
    try {
      await handleContinue();
    } finally {
      setContinuing(false);
    }
  };

  // App passes jobId; the URL is the fallback so the header still names the job.
  const shellJobId = jobId || new URLSearchParams(window.location.search).get('job');
  const isValidated = uploadState === 'validated';
  const showResult = (uploadState === 'validated' || uploadState === 'error') && previewImage;

  return (
    <StepShell jobId={shellJobId} phase={PHASE.SIGN}>
      <Card>
        <StuckDriverHelper failureCount={failureCount} pageName="DVLAPreviewPage" email={email} />

        <h2 className="hf-h2">Your driving record</h2>
        <p className="hf-intro">
          We need a copy of your DVLA record to see any points. It&rsquo;s free and takes about
          2 minutes on GOV.UK.
        </p>

        <ol className="hf-numlist">
          <li>
            <span className="hf-dot">1</span>
            <span>
              Open{' '}
              <a className="hf-link" href={GOV_UK_RECORD_URL} target="_blank" rel="noopener noreferrer">
                View your driving licence
              </a>{' '}
              on GOV.UK. You&rsquo;ll need your licence number, National Insurance number and postcode.
            </span>
          </li>
          <li>
            <span className="hf-dot">2</span>
            <span>
              Generate a <strong>check code</strong>. The summary needs one on it, or we can&rsquo;t accept it.
            </span>
          </li>
          <li>
            <span className="hf-dot">3</span>
            <span>
              Choose <strong>&ldquo;Print or save driving summary&rdquo;</strong> and save it as a PDF. It must be
              dated within the last <strong>30 days</strong>.
            </span>
          </li>
          <li>
            <span className="hf-dot">4</span>
            <span>Come back to this tab and upload it below.</span>
          </li>
        </ol>

        <Note tone="amber">
          <strong>Upload the whole PDF</strong>, with the check code on it. A cropped screenshot of just
          the code won&rsquo;t be accepted.
        </Note>

        <p className="hf-small">
          Need a hand?{' '}
          <a className="hf-link" href={DVLA_HELP_GUIDE_URL} target="_blank" rel="noopener noreferrer">
            See our step-by-step guide
          </a>
        </p>

        {/* Upload slot */}
        {uploadState === 'idle' && (
          <>
            <div
              role="button"
              tabIndex={0}
              className={`hf-upload${isDragging ? ' is-dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <span className="hf-upload-plus" aria-hidden="true">+</span>
              <div className="hf-upload-main">
                <p className="hf-upload-title">Upload your DVLA summary</p>
                <p className="hf-upload-sub">PDF is best. A photo also works (JPG or PNG, up to 10MB).</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Processing State - SESSION 82: timer and slow-processing message */}
        {(uploadState === 'uploading' || uploadState === 'processing') && (
          <div className="hf-inset hf-processing">
            <Spinner />
            <p className="hf-strong" style={{ margin: 0 }}>
              {uploadState === 'uploading' ? 'Uploading your record…' : 'Reading your DVLA record…'}
            </p>
            <p className="hf-small">Checking points, endorsements and the date. Please keep this page open.</p>

            {/* SESSION 82: Processing timer */}
            {processingElapsed > 5 && (
              <p className="hf-small">Working on it for {formatElapsedTime(processingElapsed)}</p>
            )}

            {/* SESSION 82: Slow processing warning */}
            {processingElapsed > 20 && (
              <Note tone="amber">This is taking longer than usual. Please wait.</Note>
            )}
          </div>
        )}

        {/* Result of the check, with the preview of what they uploaded */}
        {showResult && (
          <>
            {isValidated ? (
              <div className="hf-upload is-done">
                <span className="hf-upload-tick" aria-hidden="true">✓</span>
                <div className="hf-upload-main">
                  <p className="hf-upload-title">
                    Record checked · {pointsLabel(extractedData?.totalPoints || 0)}
                  </p>
                  <p className="hf-upload-sub">Your DVLA summary has been read.</p>
                </div>
                <button type="button" className="hf-btn-small" onClick={handleReset}>Change</button>
              </div>
            ) : (
              <p className="hf-note hf-note-error">
                <strong>This document didn&rsquo;t pass the check.</strong>{' '}
                {errorMessage}
              </p>
            )}

            <div className="hf-preview">
              <img
                src={`data:image/jpeg;base64,${previewImage}`}
                alt="Uploaded DVLA check preview"
              />
            </div>

            {uploadState === 'error' && (
              <button type="button" className="hf-btn-secondary" onClick={handleReset}>
                Try a different document
              </button>
            )}
          </>
        )}

        {/* SESSION 82: Error state without preview (e.g., timeout before upload completed) */}
        {uploadState === 'error' && !previewImage && (
          <>
            <p className="hf-note hf-note-error">
              <strong>Something went wrong.</strong>{' '}
              {errorMessage || 'Failed to process your document. Please try again.'}
            </p>
            <PrimaryButton onClick={handleReset}>Try again</PrimaryButton>
          </>
        )}
      </Card>

      {/* What the record says, the excess, and the way on to signing */}
      {showResult && isValidated && (
        <Card>
          <h2 className="hf-h2">What your record shows</h2>

          {extractedData && (
            <div className="hf-summary">
              <SummaryRow label="Name" value={extractedData.driverName || 'Not detected'} />
              {/* SESSION 96: Show expected ending from Idenfy (reliable source) */}
              <SummaryRow label="Licence number" value={`XXXXXXXX${licenseEnding || '????????'}`} />
              <SummaryRow label="Check code" value={extractedData.checkCode || 'Not detected'} />
              <SummaryRow
                label="Dated"
                value={
                  `${extractedData.dateGenerated || 'Not detected'}` +
                  (extractedData.ageInDays ? ` (${extractedData.ageInDays} days ago)` : '')
                }
              />
              <SummaryRow label="Penalty points" value={pointsLabel(extractedData.totalPoints || 0)} strong />
            </div>
          )}

          {extractedData?.endorsements && extractedData.endorsements.length > 0 && (
            <div className="hf-stack">
              <p className="hf-label" style={{ margin: 0 }}>Endorsements</p>
              <ul className="hf-bullets">
                {extractedData.endorsements.map((endorsement, idx) => (
                  <li key={idx}>
                    <strong>{endorsement.code}</strong>
                    {endorsement.description ? ` ${endorsement.description}` : ''} ({pointsLabel(endorsement.points)})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* SIMPLIFIED EXCESS DISPLAY - Just amount and explanation */}
          {insuranceDecision?.approved && calculatedExcess && (
            <div className="hf-inset hf-inset-lg">
              <p className="hf-small">Your insurance excess for this hire</p>
              <p className="hf-big-figure">{calculatedExcess.formatted}</p>
              <p className="hf-small">
                {getExcessExplanation()} This is what you&rsquo;d pay if there&rsquo;s a claim.
              </p>
            </div>
          )}

          {/* Insurance decision (for non-approved cases) */}
          {insuranceDecision && !insuranceDecision.approved && (
            insuranceDecision.manualReview ? (
              <Note tone="amber">
                <strong>Our insurer needs to review this.</strong>{' '}
                {insuranceDecision.reasons?.join('. ')}
              </Note>
            ) : (
              <Note tone="error">
                <strong>Our insurer can&rsquo;t cover this.</strong>{' '}
                {insuranceDecision.reasons?.join('. ')}
              </Note>
            )
          )}

          {/* One step left. This is the LAST manual step before signing, and
              the "validated" state renders before Continue is pressed — so a
              returning driver reads it as "done" and closes the tab (Cameron
              Williams-Hill / 16618, Sep 2026). Nothing joins them to the hire
              until they sign, so say so here, in their face. */}
          <Note tone="amber">
            <strong>Not finished yet:</strong> one last step, signing the hire agreement. You&rsquo;re
            not added to this hire until you sign.
          </Note>

          {insuranceDecision?.approved && (
            <PrimaryButton onClick={handleContinueClick} busy={continuing} busyLabel="Saving…">
              Continue to signature
            </PrimaryButton>
          )}

          {insuranceDecision?.manualReview && (
            <PrimaryButton onClick={handleContinueClick} busy={continuing} busyLabel="Saving…">
              Submit for review
            </PrimaryButton>
          )}
        </Card>
      )}
    </StepShell>
  );
};

export default DVLAPreviewPage;